import axios, { type AxiosInstance } from 'axios';
import { getSessionToken, handleSessionExpired } from './sessionStore';

/**
 * The data-room API base. RELATIVE by default (`/dataroom`) — NEVER a hardcoded stage host (the
 * legacy enclave pinned its stage API host directly in source; we do not repeat that). In production
 * nginx serves this path co-located with the /room/ bundle; in dev Vite proxies it.
 */
export const API_BASE = import.meta.env.VITE_DATAROOM_API_BASE || '/dataroom';

/** Multipart field name for an uploaded document. Contract left this implicit — assumed `file`. */
export const DOC_FIELD = 'file';

/**
 * The data-room API client. Every request carries the in-memory session bearer (if a session
 * exists); the POST /v1/session exchange runs BEFORE any session exists, so it sends no bearer. A
 * 401 rejects closed (the session is treated as expired) — the portal never silently degrades to an
 * unauthenticated-but-mounted state.
 */
export const dataroomClient: AxiosInstance = axios.create({
  baseURL: API_BASE,
  timeout: 30_000,
  headers: { Accept: 'application/json' },
});

dataroomClient.interceptors.request.use((config) => {
  const token = getSessionToken();
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  return config;
});

dataroomClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      handleSessionExpired(); // reject-closed
    }
    return Promise.reject(error);
  },
);
